import React from 'react';
import { SparklesIcon } from './Icons';
import { UI_CONFIG } from '../../config';

const CREDIT = UI_CONFIG.CREDIT_LABEL || 'Tín dụng';

interface OutOfCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigateToStore: () => void;
  /** Tên tính năng đang chạy (vd: "tạo giáo án"). Có thì wording personal hơn. */
  featureName?: string;
  /**
   * Chỉ có giá trị khi backend chạy pricing_enforcement_mode='plan' (Phase 1).
   * 'window' = chạm cửa sổ trượt ngắn hạn (vài giờ) — KHÔNG phải hết khả năng
   * tháng; 'cycle' = hết hạn mức tháng, giống ý nghĩa "hết khả năng AI" cũ.
   * undefined = chế độ legacy (mặc định) → giữ nguyên wording cũ 100%.
   */
  reason?: 'window' | 'cycle';
  /** ISO 8601 — thời điểm hạn mức đặt lại. Chỉ có khi reason='window'|'cycle'. */
  resetAt?: string;
}

function formatResetTime(resetAt?: string): string | null {
  if (!resetAt) return null;
  const d = new Date(resetAt);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
  if (sameDay) return `${time} hôm nay`;
  return `${time} ngày ${d.toLocaleDateString('vi-VN')}`;
}

/**
 * Modal "hết khả năng AI tháng này" — thiết kế theo nguyên tắc:
 *  - KHÔNG dùng từ "hết tín dụng" (tạo stress, cảm giác mất tiền)
 *  - Thay bằng "đã dùng hết khả năng AI tháng này" → cảm giác đã LÀM ĐƯỢC nhiều việc
 *  - Đưa ra 3 lựa chọn rõ ràng (action ladder): nâng gói (best value) → nạp lẻ (nhanh) → để sau
 *  - Có dòng đồng cảm + reassurance để giảm friction tâm lý
 */
const OutOfCreditsModal: React.FC<OutOfCreditsModalProps> = ({ isOpen, onClose, onNavigateToStore, featureName, reason, resetAt }) => {
  if (!isOpen) return null;

  const featureContext = featureName
    ? `Để tiếp tục ${featureName.toLowerCase()} và các công cụ AI khác,`
    : 'Để tiếp tục sáng tạo cùng AI BookAI,';

  // Chạm cửa sổ trượt ngắn hạn: đây KHÔNG phải "hết khả năng tháng này" —
  // wording phải nhẹ nhàng hơn nhiều, tự đặt lại trong vài giờ, không cần ép nâng gói ngay.
  const isWindowLimit = reason === 'window';
  const resetTimeLabel = formatResetTime(resetAt);

  const headline = isWindowLimit
    ? 'Bạn đang dùng AI khá dồn dập'
    : 'Bạn đã dùng hết khả năng AI tháng này';

  const description = isWindowLimit
    ? `Hệ thống tạm giữ nhịp để đảm bảo tốc độ ổn định cho mọi người.${resetTimeLabel ? ` Bạn có thể dùng tiếp lúc ${resetTimeLabel}.` : ''} Nếu cần dùng ngay, hãy nâng gói để có hạn mức rộng hơn.`
    : `Tuyệt vời — bạn đã tận dụng tối đa BookAI. ${featureContext} hãy nâng gói hoặc nạp thêm khả năng AI ngay nhé.`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4"
      onClick={onClose}
      aria-modal="true"
      role="dialog"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-7 text-center bg-gradient-to-br from-amber-50 to-orange-50 border-b border-amber-100">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg shadow-amber-200">
            <SparklesIcon className="h-7 w-7 text-white" />
          </div>
          <h3 className="mt-4 text-xl font-black text-slate-900">
            {headline}
          </h3>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">
            {description}
          </p>
        </div>

        {!isWindowLimit && (
          <div className="px-6 py-5 bg-slate-50/40 border-b border-slate-100">
            <div className="flex items-start gap-3 text-left">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
                <i className="ti ti-rocket text-teal-600 text-base" />
              </div>
              <div className="flex-1">
                <div className="text-xs font-black text-slate-700 uppercase tracking-wider">Gợi ý nhanh</div>
                <div className="text-xs text-slate-600 mt-1 leading-relaxed">
                  Nâng lên gói <strong className="text-teal-700">Chuyên nghiệp</strong> để có thêm{' '}
                  <strong>~70 giáo án/tháng</strong> chỉ với <strong>199.000đ</strong> — tiết kiệm hơn nạp lẻ.
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="px-6 py-5 flex flex-col gap-2">
          <button
            type="button"
            onClick={onNavigateToStore}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 bg-gradient-to-r from-teal-600 to-teal-500 text-sm font-black text-white hover:shadow-lg hover:shadow-teal-200 active:scale-[0.98] transition-all uppercase tracking-wider"
          >
            <i className="ti ti-sparkles text-base" />
            {isWindowLimit ? 'Nâng gói để dùng ngay' : 'Xem các gói AI'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-full inline-flex justify-center rounded-xl px-4 py-2.5 text-sm font-bold text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors"
          >
            {isWindowLimit ? 'Để sau, tôi đợi được' : 'Để sau'}
          </button>
        </div>

        <div className="px-6 pb-4 -mt-1">
          <p className="text-[11px] text-slate-400 text-center leading-relaxed">
            {isWindowLimit
              ? 'Đây là giới hạn tốc độ ngắn hạn, không phải hết hạn mức tháng — mọi thứ trở lại bình thường rất nhanh.'
              : <>Khả năng AI sẽ làm mới mỗi đầu chu kỳ thanh toán. {CREDIT} dư có thể được bảo lưu sang tháng sau theo từng gói.</>}
          </p>
        </div>
      </div>
    </div>
  );
};

export default OutOfCreditsModal;
